<template>
    <div class="deregister-cooldown-container">
        <div class="content">
            <div class="icon">⏳</div>
            <div class="title">您的账号注销申请审核通过</div>
            <div class="subtitle">
                将在 <span class="highlight">{{ effectiveTimeText }}</span> 执行注销
            </div>

            <div class="progress-card">
                <div class="progress-head">
                    <span class="label">冷静期进度</span>
                    <span class="remain">剩余 {{ remainDays }} 天</span>
                </div>
                <div class="progress-track">
                    <div class="progress-fill" :style="{width: progressPercent + '%'}"></div>
                </div>
            </div>

            <div class="notice">
                <span class="notice-title">什么是注销冷静期？</span>{{ noticeBody }}
            </div>

            <div class="btn-bar">
                <button
                    type="button" class="withdraw"
                    @click="openWithdraw"
                >撤回注销申请</button>
            </div>
        </div>

        <veui-dialog
            ui="narrow"
            title="撤回注销申请"
            :open.sync="withdrawDialogOpen"
        >
            确认撤回本次注销申请吗？撤回后资质将保持正常。
            <template #foot>
                <veui-button @click="withdrawDialogOpen = false">取消</veui-button>
                <veui-button
                    ui="primary"
                    :loading="withdrawing"
                    @click="confirmWithdraw"
                >
                    确认撤回
                </veui-button>
            </template>
        </veui-dialog>
    </div>
</template>

<script>
import {Dialog, Button} from 'veui';
import {getDeregisterStatus, withdrawDeregister} from '@/request/deregister';

// 冷静期总天数
const COOLING_DAYS = 7;

export default {
    name: 'DeregisterCooldownPage',

    components: {
        'veui-dialog': Dialog,
        'veui-button': Button
    },

    props: {
        statusInfo: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            effectiveTime: '',
            remainSeconds: 0,
            withdrawDialogOpen: false,
            withdrawing: false,
            timer: null,
            noticeBody: '自申请通过起 7 日内为冷静期，账号及关联数据将被冻结，暂停对外提供咨询服务；'
                + '您可在此期间随时撤回注销申请，逾期系统将自动执行注销，且注销后数据不可恢复。'
        };
    },

    computed: {
        // 执行注销时间，展示到分钟
        effectiveTimeText() {
            if (!this.effectiveTime) {
                return '--';
            }
            return this.effectiveTime.slice(0, 16);
        },
        // 剩余天数（向上取整）
        remainDays() {
            return Math.max(0, Math.ceil(this.remainSeconds / 86400));
        },
        // 冷静期进度百分比
        progressPercent() {
            const total = COOLING_DAYS * 86400;
            const percent = (1 - this.remainSeconds / total) * 100;
            return Math.min(100, Math.max(0, percent));
        }
    },

    mounted() {
        if (this.statusInfo && this.statusInfo.effective_time) {
            this.applyStatus(this.statusInfo);
        }
        else {
            this.fetchStatus();
        }
    },

    beforeDestroy() {
        this.clearTimer();
    },

    methods: {
        applyStatus(info) {
            this.effectiveTime = info.effective_time || '';
            this.remainSeconds = Number(info.remain_seconds) || 0;
            this.startCountdown();
        },

        async fetchStatus() {
            try {
                const res = await getDeregisterStatus();
                if (+res.code === 0 && res.data && Number(res.data.status) === 1) {
                    this.applyStatus(res.data);
                }
                else {
                    // 已不在冷静期，返回认证页
                    this.$emit('withdrawn');
                }
            }
            catch (error) {
                this.$toast.error('获取注销状态失败，请重试！');
            }
        },

        startCountdown() {
            this.clearTimer();
            this.timer = setInterval(() => {
                if (this.remainSeconds > 0) {
                    this.remainSeconds -= 1;
                }
                else {
                    this.clearTimer();
                }
            }, 1000);
        },

        clearTimer() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        },

        openWithdraw() {
            this.withdrawDialogOpen = true;
        },

        async confirmWithdraw() {
            if (this.withdrawing) {
                return;
            }
            this.withdrawing = true;
            try {
                const res = await withdrawDeregister();
                if (+res.code === 0) {
                    this.$toast.success('已撤回注销申请');
                    this.withdrawDialogOpen = false;
                    this.$emit('withdrawn');
                }
                else {
                    this.$toast.error(res.msg || '撤回失败，请重试');
                }
            }
            catch (error) {
                this.$toast.error('撤回失败，请重试！');
            }
            finally {
                this.withdrawing = false;
            }
        }
    }
};
</script>

<style lang="less" scoped>
.deregister-cooldown-container {
    .content {
        max-width: 720px;
        margin: 0 auto;
        padding: 40px 30px 48px;
        background: #fff;
        border-radius: 8px;
        text-align: center;
    }

    .icon {
        width: 72px;
        height: 72px;
        margin: 0 auto;
        border-radius: 50%;
        background: #fdf0dd;
        font-size: 36px;
        line-height: 72px;
        text-align: center;
    }

    .title {
        margin-top: 20px;
        color: #1e1f24;
        font-family: "PingFang SC";
        font-size: 22px;
        font-weight: 700;
        line-height: 30px;
    }

    .subtitle {
        margin-top: 12px;
        color: #848691;
        font-size: 15px;
        line-height: 22px;

        .highlight {
            color: #f5483b;
            font-weight: 600;
        }
    }

    .progress-card {
        margin: 28px auto 0;
        padding: 20px;
        background: #f7f8fa;
        border: 1px solid #eef0f4;
        border-radius: 8px;
        text-align: left;

        .progress-head {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .label {
                color: #848691;
                font-size: 15px;
            }

            .remain {
                color: #05f;
                font-size: 16px;
                font-weight: 600;
            }
        }

        .progress-track {
            margin-top: 14px;
            height: 6px;
            border-radius: 3px;
            background: #e6e9f0;
            overflow: hidden;

            .progress-fill {
                height: 100%;
                border-radius: 3px;
                background: #05f;
                transition: width .3s ease;
            }
        }
    }

    .notice {
        margin: 24px auto 0;
        padding: 16px 18px;
        background: #fef6e9;
        border-radius: 8px;
        color: #9a6a1c;
        font-size: 14px;
        line-height: 24px;
        text-align: left;

        .notice-title {
            font-weight: 600;
        }
    }

    .btn-bar {
        margin-top: 32px;
        display: flex;
        justify-content: center;

        .withdraw {
            display: flex;
            height: 42px;
            padding: 0 40px;
            justify-content: center;
            align-items: center;
            border-radius: 5px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            color: #fff;
            background-color: #f7695a;
            border: none;
        }
    }
}
</style>
